import * as React from 'react';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

// @mui
import {
  Typography,
  Box,
  Stack,
  TextField,
  Button,
  Modal,
  Checkbox,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import TuneIcon from '@mui/icons-material/Tune';
import SecurityIcon from '@mui/icons-material/Security';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import GridOnIcon from '@mui/icons-material/GridOn';
import PowerIcon from '@mui/icons-material/Power';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import { cores, campoEscuro } from '../styles/styleUtils';

// ── Estilos ──────────────────────────────────────────────────────────
const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '60vw',
  height: '95vh',
  '@media screen and (max-width: 600px)': { width: '100vw', height: '90vh' },
  bgcolor: 'transparent',
  border: `1px solid ${cores.chumbo}`,
  borderTop: `4px solid ${cores.laranja}`,
  boxShadow: 24,
  p: 2,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  background: `linear-gradient(180deg, ${cores.painelTopo} 0%, ${cores.painelBase} 100%)`,
  color: cores.textoClaro,
};

const styleTextField = {
  ...campoEscuro('26vw'),
  '@media screen and (max-width: 600px)': { width: '41vw' },
};

const styleTextFieldSm = {
  ...campoEscuro('12vw'),
  '@media screen and (max-width: 600px)': { width: '38vw' },
};

// Cartão sutil pra agrupar cada seção do formulário sobre o painel escuro
// (mesmo padrão usado no AddKit/AddKitOffGrid/AddKitBombeamento)
const sxCard = { backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: 2, p: 2, mb: 2 };

// ── Objetos vazios ────────────────────────────────────────────────────
const emptyModulo = {
  modelo: '', marca: '', potencia: '', peso: '', altura: '', largura: '',
  tensaonominal: '', tensaocircuitoaberto: '', correntenominal: '', correntecurtocircuito: '',
  garantiafabrica: '', garantiaeficiencia: '',
  depreciacaoprimeiro: '', depreciacaodemais: '',
  foto: '', datasheet: null, registroinmetro: null,
  ativo: true,
};

const emptyInversor = {
  modelo: '', fabricante: '', sistemamonitoramento: '', garantia: '',
  potencia: '', peso: '', altura: '', largura: '',
  tensaonominal: '', correntenominal: '', faixatensaomppt: '', eficiencia: '',
  numeromppts: '', correntepormppt: '', disjuntorsugerido: '', bitolacaboca: '',
  tensao: '',
  foto: '', datasheet: null, registroinmetro: null,
  ativo: true,
};

const emptyBateria = {
  modelo: '', fabricante: '', potencia: '', autonomia: '',
  garantia: '', vidautil: '', tensaonominal: '', correntenominal: '',
  disjuntor: '', bitolacabo: '', peso: '', altura: '', largura: '',
  foto: '', datasheet: null, registroinmetro: null,
  ativo: true,
};

const emptyInversorBomb = {
  modelo: '', fabricante: '', potencia: '', garantia: '',
  tensaonominal: '', correntenominal: '', disjuntor: '', bitolacabo: '',
  peso: '', altura: '', largura: '',
  foto: '', datasheet: null, registroinmetro: null,
  ativo: true,
};

// ── Componente auxiliar de anexo PDF ─────────────────────────────────
const AnexoPdf = ({ label, valor, onChange }) => {
  const handleClick = () => {
    if (valor) {
      // Abrir PDF em nova aba
      const byteCharacters = atob(valor.split(',')[1]);
      const byteNumbers = Array.from(byteCharacters).map((c) => c.charCodeAt(0));
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf';
    input.style.display = 'none';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onloadend = () => onChange(reader.result);
      reader.readAsDataURL(file);
    };
    document.body.appendChild(input);
    input.click();
  };

  const handleAnexar = (e) => {
    e.stopPropagation();
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf';
    input.style.display = 'none';
    input.onchange = (ev) => {
      const file = ev.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onloadend = () => onChange(reader.result);
      reader.readAsDataURL(file);
    };
    document.body.appendChild(input);
    input.click();
  };

  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <Typography variant="body2" sx={{ width: 160, color: 'rgba(255,255,255,0.75)' }}>{label}:</Typography>
      {valor ? (
        <>
          <Button size="small" variant="outlined" onClick={handleClick} sx={{ color: cores.textoClaro, borderColor: cores.laranja, '&:hover': { borderColor: cores.laranjaEscuro, backgroundColor: 'rgba(209,102,9,0.15)' } }}>
            Visualizar PDF
          </Button>
          <Button size="small" variant="outlined" color="warning" onClick={handleAnexar}>
            Substituir
          </Button>
        </>
      ) : (
        <Button size="small" variant="contained" onClick={handleAnexar} sx={{ backgroundColor: cores.laranja, '&:hover': { backgroundColor: cores.laranjaEscuro } }}>
          Anexar
        </Button>
      )}
    </Stack>
  );
};

// ── Componente principal ──────────────────────────────────────────────
// CORRIGIDO (deployment Marcki — bug de cursor/foco perdido a cada tecla):
// Rodape, FotoBox e SecaoCard estavam definidos DENTRO da função AddProduto.
// Isso faz o React recriar essas funções a cada re-render (ou seja, a cada
// tecla digitada em qualquer campo do formulário), o que faz o React tratar
// os campos internos como componentes totalmente novos — destruindo e
// recriando os elementos <input> reais do DOM a cada tecla, o que derruba o
// foco/cursor mesmo com o valor digitado aparecendo certo. A correção é
// definir esses componentes UMA VEZ, fora do componente pai, recebendo por
// props tudo que antes vinha do escopo (produto, handlers, etc.).
const Rodape = ({ onClose, onConfirm }) => (
  <Stack direction="row" justifyContent="space-between" sx={{ mt: 'auto', pt: 2, pb: 1 }}>
    <Button variant="contained" onClick={onClose} color="error">Cancelar</Button>
    <Button variant="contained" color="success" onClick={onConfirm}>Salvar</Button>
  </Stack>
);

const FotoBox = ({ foto, onEscolherImagem }) => (
  <Box display="flex" justifyContent="center" alignItems="center" my={1} flexDirection="column">
    <div style={{ maxWidth: 150 }}>
      {!foto || foto === ''
        ? <Button variant="contained" onClick={onEscolherImagem} sx={{ backgroundColor: cores.laranja, '&:hover': { backgroundColor: cores.laranjaEscuro } }}>Escolher Imagem</Button>
        : <a href="#img" onClick={(e) => { e.preventDefault(); onEscolherImagem(); }}>
            <img src={foto} alt="Produto" style={{ maxWidth: 150 }} />
          </a>
      }
    </div>
  </Box>
);

const SecaoCard = ({ titulo, icone: Icone, children }) => (
  <Box sx={sxCard}>
    <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
      {Icone ? <Icone fontSize="small" sx={{ color: cores.laranja }} /> : null}
      <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.6)' }}>{titulo}</Typography>
    </Stack>
    {children}
  </Box>
);

const AddProduto = React.forwardRef((props, ref) => {
  const [produto, setProduto] = useState({});

  useEffect(() => {
    if (props.produto) {
      setProduto(props.produto);
    } else if (props.radioValue === 'modulos') {
      setProduto(emptyModulo);
    } else if (props.radioValue === 'inversores') {
      setProduto(emptyInversor);
    } else if (props.radioValue === 'baterias') {
      setProduto(emptyBateria);
    } else if (props.radioValue === 'inversoresbomb') {
      setProduto(emptyInversorBomb);
    }
  }, [props.produto, props.radioValue]);

  const handleChange = (e) => {
    setProduto({ ...produto, [e.target.id]: e.target.value });
  };

  // Imagem
  const handleButtonClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.png,.jpg,.jpeg';
    input.style.display = 'none';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onloadend = () => setProduto({ ...produto, foto: reader.result });
      reader.readAsDataURL(file);
    };
    document.body.appendChild(input);
    input.click();
  };

  const handleConfirm = () => {
    props.onConfirm(produto);
    props.onClose();
  };

  // ════════════════════════════════════════════════════════════════════
  // MÓDULO
  // ════════════════════════════════════════════════════════════════════
  if (props.radioValue === 'modulos') return (
    <Modal open={props.open} onClose={props.handleClose}>
      <Box sx={style}>
        <Typography variant="h4" sx={{ mb: 1 }}>
          <GridOnIcon fontSize="large" sx={{ verticalAlign: 'middle', mr: 1, color: cores.laranja }} />
          Módulo FV
        </Typography>

        <SecaoCard titulo="Identificação" icone={DescriptionIcon}>
          <Stack direction="row" gap={2} flexWrap="wrap">
            <TextField id="modelo"  label="Modelo"  variant="outlined" sx={styleTextField} value={produto.modelo  || ''} onChange={handleChange} />
            <TextField id="marca"   label="Marca"   variant="outlined" sx={styleTextField} value={produto.marca   || ''} onChange={handleChange} />
          </Stack>
        </SecaoCard>

        <SecaoCard titulo="Parâmetros Elétricos" icone={FlashOnIcon}>
          <Stack direction="row" gap={2} flexWrap="wrap">
            <TextField id="potencia"               label="Potência (Wp)"              inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.potencia               || ''} onChange={handleChange} />
            <TextField id="tensaonominal"          label="Tensão Nominal (V)"         inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.tensaonominal          || ''} onChange={handleChange} />
            <TextField id="tensaocircuitoaberto"   label="Tensão Circuito Aberto (V)" inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.tensaocircuitoaberto   || ''} onChange={handleChange} />
            <TextField id="correntenominal"        label="Corrente Nominal (A)"       inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.correntenominal        || ''} onChange={handleChange} />
            <TextField id="correntecurtocircuito"  label="Corrente Curto (A)"         inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.correntecurtocircuito  || ''} onChange={handleChange} />
          </Stack>
        </SecaoCard>

        <SecaoCard titulo="Dimensões e Peso" icone={TuneIcon}>
          <Stack direction="row" gap={2} flexWrap="wrap">
            <TextField id="peso"    label="Peso (kg)" inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.peso    || ''} onChange={handleChange} />
            <TextField id="altura"  label="Altura (m)" inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.altura  || ''} onChange={handleChange} />
            <TextField id="largura" label="Largura (m)" inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.largura || ''} onChange={handleChange} />
          </Stack>
        </SecaoCard>

        <SecaoCard titulo="Garantias" icone={SecurityIcon}>
          <Stack direction="row" gap={2} flexWrap="wrap">
            <TextField id="garantiafabrica"     label="Garantia Fábrica (anos)"    inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.garantiafabrica     || ''} onChange={handleChange} />
            <TextField id="garantiaeficiencia"  label="Garantia Eficiência (anos)" inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.garantiaeficiencia  || ''} onChange={handleChange} />
          </Stack>
        </SecaoCard>

        <SecaoCard titulo="Depreciação" icone={AccessTimeIcon}>
          <Stack direction="row" gap={2} flexWrap="wrap">
            <TextField id="depreciacaoprimeiro" label="Depreciação 1º ano (%)"     inputProps={{ type: 'number', step: '0.01' }} variant="outlined" sx={styleTextFieldSm} value={produto.depreciacaoprimeiro || ''} onChange={handleChange} />
            <TextField id="depreciacaodemais"   label="Depreciação Demais anos (%)" inputProps={{ type: 'number', step: '0.01' }} variant="outlined" sx={styleTextFieldSm} value={produto.depreciacaodemais   || ''} onChange={handleChange} />
          </Stack>
        </SecaoCard>

        <SecaoCard titulo="Anexos" icone={AttachFileIcon}>
          <Stack gap={1}>
            <AnexoPdf label="Datasheet"        valor={produto.datasheet}      onChange={(v) => setProduto({ ...produto, datasheet: v })} />
            <AnexoPdf label="Registro INMETRO" valor={produto.registroinmetro} onChange={(v) => setProduto({ ...produto, registroinmetro: v })} />
          </Stack>
        </SecaoCard>

        <Stack direction="row" alignItems="center">
          <Checkbox checked={produto.ativo} onChange={() => setProduto({ ...produto, ativo: !produto.ativo })} />
          <Typography>Ativo</Typography>
        </Stack>

        <FotoBox foto={produto.foto} onEscolherImagem={handleButtonClick} />
        <Rodape onClose={() => props.onClose()} onConfirm={handleConfirm} />
      </Box>
    </Modal>
  );

  // ════════════════════════════════════════════════════════════════════
  // INVERSOR
  // ════════════════════════════════════════════════════════════════════
  if (props.radioValue === 'inversores') return (
    <Modal open={props.open} onClose={props.handleClose}>
      <Box sx={style}>
        <Typography variant="h4" sx={{ mb: 1 }}>
          <PowerIcon fontSize="large" sx={{ verticalAlign: 'middle', mr: 1, color: cores.laranja }} />
          Inversor
        </Typography>

        <SecaoCard titulo="Identificação" icone={DescriptionIcon}>
          <Stack direction="row" gap={2} flexWrap="wrap">
            <TextField id="modelo"               label="Modelo"                variant="outlined" sx={styleTextField} value={produto.modelo               || ''} onChange={handleChange} />
            <TextField id="fabricante"           label="Fabricante"            variant="outlined" sx={styleTextField} value={produto.fabricante           || ''} onChange={handleChange} />
            <TextField id="sistemamonitoramento" label="Sistema Monitoramento" variant="outlined" sx={styleTextField} value={produto.sistemamonitoramento || ''} onChange={handleChange} />
          </Stack>
        </SecaoCard>

        <SecaoCard titulo="Parâmetros Elétricos" icone={FlashOnIcon}>
          <Stack direction="row" gap={2} flexWrap="wrap">
            <TextField id="potencia"          label="Potência (kW)"          inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.potencia          || ''} onChange={handleChange} />
            <TextField id="tensao"            label="Tensão Funcionamento (V)" inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.tensao            || ''} onChange={handleChange} />
            <TextField id="tensaonominal"     label="Tensão Nominal (V)"     inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.tensaonominal     || ''} onChange={handleChange} />
            <TextField id="correntenominal"   label="Corrente Nominal (A)"   inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.correntenominal   || ''} onChange={handleChange} />
            <TextField id="faixatensaomppt"   label="Faixa Tensão MPPT (V)"  variant="outlined"              sx={styleTextFieldSm} value={produto.faixatensaomppt   || ''} onChange={handleChange} />
            <TextField id="eficiencia"        label="Eficiência (%)"         inputProps={{ type: 'number', step: '0.01' }} variant="outlined" sx={styleTextFieldSm} value={produto.eficiencia        || ''} onChange={handleChange} />
            <TextField id="numeromppts"       label="Nº de MPPTs"            inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.numeromppts       || ''} onChange={handleChange} />
            <TextField id="correntepormppt"   label="Corrente por MPPT (A)"  inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.correntepormppt   || ''} onChange={handleChange} />
            <TextField id="disjuntorsugerido" label="Disjuntor Sugerido (A)" inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.disjuntorsugerido || ''} onChange={handleChange} />
            <TextField id="bitolacaboca"      label="Bitola Cabo CA (mm²)"   inputProps={{ type: 'number', step: '0.01' }} variant="outlined" sx={styleTextFieldSm} value={produto.bitolacaboca      || ''} onChange={handleChange} />
          </Stack>
        </SecaoCard>

        <SecaoCard titulo="Garantia" icone={SecurityIcon}>
          <TextField id="garantia" label="Garantia (anos)" inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.garantia || ''} onChange={handleChange} />
        </SecaoCard>

        <SecaoCard titulo="Anexos" icone={AttachFileIcon}>
          <Stack gap={1}>
            <AnexoPdf label="Datasheet"        valor={produto.datasheet}      onChange={(v) => setProduto({ ...produto, datasheet: v })} />
            <AnexoPdf label="Registro INMETRO" valor={produto.registroinmetro} onChange={(v) => setProduto({ ...produto, registroinmetro: v })} />
          </Stack>
        </SecaoCard>

        <Stack direction="row" alignItems="center">
          <Checkbox checked={produto.ativo} onChange={() => setProduto({ ...produto, ativo: !produto.ativo })} />
          <Typography>Ativo</Typography>
        </Stack>

        <FotoBox foto={produto.foto} onEscolherImagem={handleButtonClick} />
        <Rodape onClose={() => props.onClose()} onConfirm={handleConfirm} />
      </Box>
    </Modal>
  );

  // ════════════════════════════════════════════════════════════════════
  // BATERIA
  // ════════════════════════════════════════════════════════════════════
  if (props.radioValue === 'baterias') return (
    <Modal open={props.open} onClose={props.handleClose}>
      <Box sx={style}>
        <Typography variant="h4" sx={{ mb: 1 }}>
          <BatteryChargingFullIcon fontSize="large" sx={{ verticalAlign: 'middle', mr: 1, color: cores.laranja }} />
          Bateria
        </Typography>

        <SecaoCard titulo="Identificação" icone={DescriptionIcon}>
          <Stack direction="row" gap={2} flexWrap="wrap">
            <TextField id="modelo"     label="Modelo"     variant="outlined" sx={styleTextField} value={produto.modelo     || ''} onChange={handleChange} />
            <TextField id="fabricante" label="Fabricante" variant="outlined" sx={styleTextField} value={produto.fabricante || ''} onChange={handleChange} />
          </Stack>
        </SecaoCard>

        <SecaoCard titulo="Parâmetros Elétricos" icone={FlashOnIcon}>
          <Stack direction="row" gap={2} flexWrap="wrap">
            <TextField id="potencia"        label="Potência (kW)"          inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.potencia        || ''} onChange={handleChange} />
            <TextField id="autonomia"       label="Autonomia (kWh)"        inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.autonomia       || ''} onChange={handleChange} />
            <TextField id="tensaonominal"   label="Tensão Nominal (V)"     inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.tensaonominal   || ''} onChange={handleChange} />
            <TextField id="correntenominal" label="Corrente Nominal (A)"   inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.correntenominal || ''} onChange={handleChange} />
            <TextField id="disjuntor"       label="Disjuntor (A)"          inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.disjuntor       || ''} onChange={handleChange} />
            <TextField id="bitolacabo"      label="Bitola do Cabo (mm²)"   inputProps={{ type: 'number', step: '0.01' }} variant="outlined" sx={styleTextFieldSm} value={produto.bitolacabo      || ''} onChange={handleChange} />
          </Stack>
        </SecaoCard>

        <SecaoCard titulo="Garantia e Vida Útil" icone={SecurityIcon}>
          <Stack direction="row" gap={2} flexWrap="wrap">
            <TextField id="garantia" label="Garantia (anos)" inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.garantia || ''} onChange={handleChange} />
            <TextField id="vidautil" label="Vida Útil (ciclos)" inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.vidautil || ''} onChange={handleChange} />
          </Stack>
        </SecaoCard>

        <SecaoCard titulo="Anexos" icone={AttachFileIcon}>
          <Stack gap={1}>
            <AnexoPdf label="Datasheet"        valor={produto.datasheet}      onChange={(v) => setProduto({ ...produto, datasheet: v })} />
            <AnexoPdf label="Registro INMETRO" valor={produto.registroinmetro} onChange={(v) => setProduto({ ...produto, registroinmetro: v })} />
          </Stack>
        </SecaoCard>

        <Stack direction="row" alignItems="center">
          <Checkbox checked={produto.ativo} onChange={() => setProduto({ ...produto, ativo: !produto.ativo })} />
          <Typography>Ativo</Typography>
        </Stack>

        <FotoBox foto={produto.foto} onEscolherImagem={handleButtonClick} />
        <Rodape onClose={() => props.onClose()} onConfirm={handleConfirm} />
      </Box>
    </Modal>
  );

  // ════════════════════════════════════════════════════════════════════
  // INVERSOR BOMBEAMENTO
  // ════════════════════════════════════════════════════════════════════
  if (props.radioValue === 'inversoresbomb') return (
    <Modal open={props.open} onClose={props.handleClose}>
      <Box sx={style}>
        <Typography variant="h4" sx={{ mb: 1 }}>
          <PowerIcon fontSize="large" sx={{ verticalAlign: 'middle', mr: 1, color: cores.laranja }} />
          Inversor Bombeamento
        </Typography>

        <SecaoCard titulo="Identificação" icone={DescriptionIcon}>
          <Stack direction="row" gap={2} flexWrap="wrap">
            <TextField id="modelo"     label="Modelo"     variant="outlined" sx={styleTextField} value={produto.modelo     || ''} onChange={handleChange} />
            <TextField id="fabricante" label="Fabricante" variant="outlined" sx={styleTextField} value={produto.fabricante || ''} onChange={handleChange} />
          </Stack>
        </SecaoCard>

        <SecaoCard titulo="Parâmetros Elétricos" icone={FlashOnIcon}>
          <Stack direction="row" gap={2} flexWrap="wrap">
            <TextField id="potencia"        label="Potência (CV)"        inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.potencia        || ''} onChange={handleChange} />
            <TextField id="tensaonominal"   label="Tensão Nominal (V)"   inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.tensaonominal   || ''} onChange={handleChange} />
            <TextField id="correntenominal" label="Corrente Nominal (A)" inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.correntenominal || ''} onChange={handleChange} />
            <TextField id="disjuntor"       label="Disjuntor (A)"        inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.disjuntor       || ''} onChange={handleChange} />
            <TextField id="bitolacabo"      label="Bitola do Cabo (mm²)" inputProps={{ type: 'number', step: '0.01' }} variant="outlined" sx={styleTextFieldSm} value={produto.bitolacabo      || ''} onChange={handleChange} />
          </Stack>
        </SecaoCard>

        <SecaoCard titulo="Garantia" icone={SecurityIcon}>
          <TextField id="garantia" label="Garantia (anos)" inputProps={{ type: 'number' }} variant="outlined" sx={styleTextFieldSm} value={produto.garantia || ''} onChange={handleChange} />
        </SecaoCard>

        <SecaoCard titulo="Anexos" icone={AttachFileIcon}>
          <Stack gap={1}>
            <AnexoPdf label="Datasheet"        valor={produto.datasheet}      onChange={(v) => setProduto({ ...produto, datasheet: v })} />
            <AnexoPdf label="Registro INMETRO" valor={produto.registroinmetro} onChange={(v) => setProduto({ ...produto, registroinmetro: v })} />
          </Stack>
        </SecaoCard>

        <Stack direction="row" alignItems="center">
          <Checkbox checked={produto.ativo} onChange={() => setProduto({ ...produto, ativo: !produto.ativo })} />
          <Typography>Ativo</Typography>
        </Stack>

        <FotoBox foto={produto.foto} onEscolherImagem={handleButtonClick} />
        <Rodape onClose={() => props.onClose()} onConfirm={handleConfirm} />
      </Box>
    </Modal>
  );

  return null;
});

AddProduto.propTypes = {
  produto:     PropTypes.object,
  onConfirm:   PropTypes.func,
  radioValue:  PropTypes.string,
  open:        PropTypes.bool,
  handleClose: PropTypes.func,
  onClose:     PropTypes.func,
};

export default AddProduto;